import { TreeRuleNode, TreeRuleNodeType } from "./Structure/TreeRuleNode";
import { RuleExecutable } from "../../Common/Execution/RuleExecutable";
import { DataUtilities } from "../../Common/DataUtilities";
import { IConfigStorage } from "../../Common/IConfigStorage";
import { ValidationIssue } from "../../Common/IDesignValidationProvider";
import { ExecutionData } from "../../Common/Data/ExecutionData/ExecutionData";
import { TreeRuleTestResult } from "./Structure/TreeRuleTestResult";
import { ITestingContext } from "../../Common/Testing/ITestingContext";
import { EventLog, EventLogType } from "../../Common/Event/EventLog";
import { IDataDefinition } from "../../Common/Data/Definitions/IDataDefinition";
import type { AppGlueAny } from "../../Common/AppGlueAny";

export class TreeRuleConfiguration extends RuleExecutable implements IConfigStorage {
    _id: string = DataUtilities.generateUniqueId();

    title?: string;

    constructor() {
        super();
        this._root = new TreeRuleNode();
    }

    _root: TreeRuleNode;

    get root(): TreeRuleNode {
        return this._root;
    }

    set root(node: TreeRuleNode) {
        this._root = node;
    }

    get executionResult(): IDataDefinition | (() => IDataDefinition) {
        return this._executionResult!;
    }

    set executionResult(value: IDataDefinition | (() => IDataDefinition)) {
        this._executionResult = value;

        for (let node of this.getAllChildren(this.root)) {
            // not sure if it's needed;
            if (node.nodeType === TreeRuleNodeType.RESULT) {
                node.expression.executionResult = () => {
                    return this.getRuleResult();
                };
            }
        }
    }

    run(data: ExecutionData, testContext?: ITestingContext): AppGlueAny {
        let node: TreeRuleNode = this._root;

        let subContext: TreeRuleTestResult | undefined;

        if (testContext) {
            subContext = new TreeRuleTestResult(this._id);
            testContext.addTestResultItem(subContext);
            subContext.addLogItem(new EventLog("TreeRule: start evaluating"));
            subContext.definition = this;
        }

        try {
            let v = true;
            while (v) {
                if (node.nodeType === TreeRuleNodeType.UNDEFINED) {
                    throw new Error("tree rule node is not configured");
                }

                if (node.nodeType === TreeRuleNodeType.RESULT) {
                    let res = node.expression.run(data, subContext);

                    if (subContext) {
                        subContext.addLogItem(new EventLog(`TreeRule: end with result [${res}]`));
                        subContext.result = res.toString();
                    }

                    return res;
                }

                if (node.nodeType === TreeRuleNodeType.EVALUATION) {
                    let res = node.expression.run(data, subContext);

                    if (res) {
                        node = node.trueNode!;
                    } else {
                        node = node.falseNode!;
                    }
                }
            }
        } catch (e) {
            if (subContext) {
                subContext.isError = true;
                subContext.result = e.message;
                subContext.addLogItem(new EventLog(`TreeRule: end with error [${e.message}]`, EventLogType.ERROR));
            }
            throw e;
        }
    }

    insert(node: TreeRuleNode, isTrue: boolean) {
        let newNode = new TreeRuleNode();
        newNode.setupEvaluation(this.getInputDefinition());

        node.setupEvaluation(this.getInputDefinition());

        if (isTrue) {
            newNode.trueNode = node.trueNode;
            node.trueNode = newNode;
        } else {
            newNode.falseNode = node.falseNode;
            node.falseNode = newNode;
        }
    }

    getAllChildren(node: TreeRuleNode) {
        let children: TreeRuleNode[] = [];
        if (node.nodeType !== TreeRuleNodeType.UNDEFINED) {
            children.push(node);
        }
        if (node.nodeType === TreeRuleNodeType.EVALUATION) {
            children = [...children, ...this.getAllChildren(node.trueNode!), ...this.getAllChildren(node.falseNode!)];
        }

        return children;
    }

    getAllResults() {
        let results: TreeRuleNode[] = [];

        for (let node of this.getAllChildren(this._root)) {
            if (node.nodeType === TreeRuleNodeType.RESULT) {
                results.push(node);
            }
        }

        return results;
    }

    getDesignValidationIssues(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        const allnodes = this.getAllChildren(this.root);

        for (let nodeidx in allnodes) {
            let node = allnodes[nodeidx];

            issues.push(
                ...node.getDesignValidationIssues().map((issue) => {
                    if (node.nodeType === TreeRuleNodeType.RESULT) {
                        issue.issue = `[Result ${parseInt(nodeidx, 10) + 1}]:: ${issue.issue}`;
                    } else if (node.nodeType === TreeRuleNodeType.EVALUATION) {
                        issue.issue = `[Expression ${parseInt(nodeidx, 10) + 1}]:: ${issue.issue}`;
                    }

                    return issue;
                }),
            );
        }
        return issues;
    }

    getStorageData(): object {
        let data = super.getStorageData();

        Reflect.set(data, "_rootNode", this._root.getStorageData());

        return data;
    }

    setStorageData(data: object) {
        super.setStorageData(data);

        this._root.setStorageData(Reflect.get(data, "_rootNode"));
    }
}
